<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { RouterLink } from 'vue-router'
import type {GlobalSearchResponse} from "@/types/search";
import {ref} from "vue";

interface Props {
  isVisible: boolean
  isLoading: boolean
  searchResults: GlobalSearchResponse | null
  searchTerm: string
  error: string | null
}

interface Emits {
  (e: 'close'): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const dropdownRef = ref<HTMLDivElement | null>(null)

const closeDropdown = () => {
  emit('close')
}

const formatEntityType = (entityType: string): string => {
  const typeMap: Record<string, string> = {
    'Batch': 'Batches',
    'ReturnedBatch': 'Returned Batches',
    'Customer': 'Customers',
    'Supplier': 'Suppliers',
    'Order': 'Orders',
    'Merge': 'Merges',
    'ProductSpecification': 'Product Specifications',
    'PackingList': 'Packing Lists',
    'User': 'Users',
    'Roll': 'Rolls'
  }
  return typeMap[entityType] || entityType
}

const getEntityIcon = (entityType: string): string => {
  const iconMap: Record<string, string> = {
    'Batch': 'mdi:package-variant',
    'ReturnedBatch': 'mdi:package-variant-closed',
    'Customer': 'mdi:account-group',
    'Supplier': 'mdi:truck',
    'Order': 'mdi:clipboard-list',
    'Merge': 'mdi:merge',
    'ProductSpecification': 'mdi:file-document',
    'PackingList': 'mdi:format-list-checks',
    'User': 'mdi:account',
    'Roll': 'mdi:cylinder'
  }
  return iconMap[entityType] || 'mdi:file'
}

const formatMatchedField = (field: string): string => {
  const fieldMap: Record<string, string> = {
    'BatchNumber': 'Batch Number',
    'PurchaseOrderReference': 'Purchase Order',
    'MachineNumber': 'Machine Number',
    'BusinessName': 'Business Name',
    'CustomerAccountReference': 'Account Reference',
    'SalesContactName': 'Contact Name',
    'SalesContactEmail': 'Contact Email',
    'OrderNumber': 'Order Number',
    'CustomerPurchaseOrderNumber': 'Customer PO',
    'DeliveryAddress': 'Delivery Address',
    'MergeNumber': 'Merge Number',
    'YarnColour': 'Yarn Colour',
    'YarnCount': 'Yarn Count',
    'FinishedReference': 'Finished Reference',
    'UnfinishedReference': 'Unfinished Reference',
    'AlternateReference': 'Alternate Reference',
    'DisplayedReference': 'Displayed Reference',
    'PackingListId': 'Packing List ID',
    'FirstName': 'First Name',
    'LastName': 'Last Name',
    'RollNumber': 'Roll Number',
    'OriginalBatchNumber': 'Original Batch',
    'CurrentBatchNumber': 'Current Batch'
  }
  return fieldMap[field] || field
}

const getRouteName = (entityType: string, itemId: string): string => {
  switch (entityType) {
    case 'Batch':
      return 'viewBatch'
    case 'ReturnedBatch':
      return 'returnedBatchDetails'
    case 'Customer':
      return 'customerDetails'
    case 'Supplier':
      return 'supplierDetails'
    case 'Order':
      return 'viewOrder'
    case 'Merge':
      return 'yarnDetails'
    case 'ProductSpecification':
      return 'productSpecificationDetails'
    case 'PackingList':
      return 'viewPackingList'
    case 'User':
      return 'userListView'
    case 'Roll':
      return 'viewRolls'
    default:
      return 'dashboard'
  }
}

const getRouteParams = (entityType: string, itemId: string) => {
  if (entityType === 'User') {
    return {}
  }
  return { id: itemId }
}
</script>

<template>
  <div
      v-if="isVisible"
      ref="dropdownRef"
      class="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden"
  >
    <div v-if="isLoading" class="p-4 text-center">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
      <p class="text-sm text-gray-500 mt-2">Searching...</p>
    </div>

    <div v-else-if="!isLoading && searchResults?.totalResults === 0" class="p-4 text-center">
      <Icon icon="majesticons:search" class="text-gray-400 text-2xl mx-auto mb-2" />
      <p class="text-sm text-gray-500">No results found for "{{ searchTerm }}"</p>
    </div>

    <div v-else-if="searchResults?.results" class="max-h-96 overflow-y-auto">
      <div class="p-3 border-b border-gray-100 bg-gray-50">
        <p class="text-xs text-gray-600 font-medium">
          {{ searchResults.totalResults }} result{{ searchResults.totalResults !== 1 ? 's' : '' }} found
        </p>
      </div>

      <div v-for="group in searchResults.results" :key="group.entityType" class="border-b border-gray-100 last:border-b-0">
        <div class="px-3 py-2 bg-gray-50 border-b border-gray-100">
          <h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {{ formatEntityType(group.entityType) }} ({{ group.count }})
          </h3>
        </div>

        <div class="py-1">
          <RouterLink
              v-for="item in group.items"
              :key="item.id"
              :to="{
                name: getRouteName(item.entityType, item.id),
                params: getRouteParams(item.entityType, item.id)
              }"
              @click="closeDropdown"
              class="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors duration-150 cursor-pointer group"
          >
            <div class="flex-shrink-0 mt-0.5">
              <Icon
                  :icon="getEntityIcon(item.entityType)"
                  class="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                {{ item.title }}
              </p>
              <p class="text-xs text-gray-500 mt-0.5 truncate">
                {{ item.subtitle }}
              </p>
              <p class="text-xs text-primary-600 mt-1">
                Matched: {{ formatMatchedField(item.matchedField) }}
              </p>
            </div>
            <div class="flex-shrink-0">
              <Icon icon="majesticons:chevron-right" class="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
            </div>
          </RouterLink>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="p-4 text-center">
      <Icon icon="majesticons:exclamation-circle" class="text-red-400 text-2xl mx-auto mb-2" />
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>
  </div>
</template>
